// Справка „Картови плащания" (Справки → card-payments).
//
// Това е сверката в края на деня, не просто списък:
//   отчет на заведението „карта" = С БОН
//   „край на деня" на терминала  = С БОН + ВИСЯЩИ
//   банката                      = С БОН (върнатите се нулират помежду си)
// Затова показателите се смятат от `terminals` — обобщението на BE за ЦЕЛИЯ
// период — а не от филтрирания списък: филтърът е лупа върху редовете, не
// нова истина за деня.

import { useMemo, useState } from "react";
import { Spin } from "antd";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";

import { useViewport } from "../../../hooks/useViewport";
import { useCardPaymentsReport, useBillHistoryById } from "../../../hooks/useReports";
import { ReportDatePicker } from "../../../components/ReportDatePicker";
import { useReportsDateParam } from "../useReportsDateParam";
import { dayBounds, type DayMode } from "../../../lib/businessDay";
import { useConfiguration } from "../../../hooks/useConfiguration";
import { DataTable, parseDecimal } from "../../../components/dataTable";
import type { ChecklistOption } from "../../../components/dataTable";
import type { CardTerminalSummary } from "../../../api/reportsCardPayments";
import { fmtEuro } from "../../../utils/currency";
import { exportToExcel } from "../../../utils/excelExport";
import ExportExcelButton from "./ExportExcelButton";
import BillOperationsModal from "./BillOperationsModal";
import { ReportTopBar } from "./ReportTopBar";
import { ReportKpiCard } from "./ReportKpiCard";
import { pluralLabel } from "./reportPlural";
import { TerminalsCard } from "./cardPayments/TerminalsCard";
import { useCardPaymentsColumns } from "./cardPayments/cardPaymentsColumns";
import type { NamedCardPaymentRow } from "./cardPayments/cardPaymentsColumns";

/** Статусите, за които има превод; непознат се показва както е дошъл. */
const KNOWN_STATUSES = ["consumed", "unknown", "voided", "void_failed"] as const;

type Props = { onOpenMobileMenu?: () => void; isNarrow?: boolean };

export const CardPaymentsReport = ({
  onOpenMobileMenu,
  isNarrow = false,
}: Props = {}) => {
  const { t, i18n } = useTranslation();
  const { isNarrow: viewportNarrow } = useViewport();
  const narrow = isNarrow || viewportNarrow;

  // Денят е по работното време (Конфигурация → Работно време), когато е
  // настроено; иначе — календарният ден, както преди настройката.
  const { configuration } = useConfiguration();
  const dayMode: DayMode = configuration.workingHours ? "business" : "calendar";
  const { dateOf, startOf: startOfDay, endOf: endOfDay } = dayBounds(dayMode);
  const [dateRange, setDateRange] = useReportsDateParam(
    () => [startOfDay(dateOf(dayjs())), endOfDay(dateOf(dayjs()))],
    dayMode,
  );
  const [selectedBillId, setSelectedBillId] = useState<number | null>(null);
  // Редовете СЛЕД филтрите в колоните — Excel изнася видяното, не суровия
  // отговор. Показателите горе остават за целия период: филтърът е лупа върху
  // редовете, не нова истина за деня.
  const [visibleRows, setVisibleRows] = useState<NamedCardPaymentRow[]>([]);

  const filters = useMemo(() => {
    if (!dateRange?.[0] || !dateRange?.[1]) return {};
    return {
      start_date: dateRange[0].format("YYYY-MM-DD HH:mm:ss"),
      end_date: dateRange[1].format("YYYY-MM-DD HH:mm:ss"),
    };
  }, [dateRange]);

  const { data, isLoading, isFetching, refetch } = useCardPaymentsReport(filters);
  const rows = useMemo(() => data?.rows ?? [], [data]);
  const terminals = useMemo(() => data?.terminals ?? [], [data]);

  const { data: selectedBill } = useBillHistoryById(selectedBillId);
  const isModalOpen =
    selectedBillId !== null && selectedBill?.id === selectedBillId;

  const totals = useMemo(() => {
    const sum = (pick: (summary: CardTerminalSummary) => string | number) =>
      terminals.reduce((acc, summary) => acc + parseDecimal(pick(summary)), 0);
    return {
      consumedTotal: sum((s) => s.consumed_total),
      consumedCount: sum((s) => s.consumed_count),
      unresolvedTotal: sum((s) => s.unresolved_total),
      unresolvedCount: sum((s) => s.unresolved_count),
      voidedTotal: sum((s) => s.voided_total),
      voidedCount: sum((s) => s.voided_count),
    };
  }, [terminals]);

  const statusLabel = (status: string) =>
    (KNOWN_STATUSES as readonly string[]).includes(status)
      ? t(`reportsPage.cardPaymentsStatus_${status}`)
      : status;

  const mountLabel = (mount: string) =>
    mount === "pc" || mount === "fiscal"
      ? t(`reportsPage.cardPaymentsMount_${mount}`)
      : mount;

  // Опциите на филтъра идват от РЕАЛНО срещнатите статуси — така непознат
  // статус от гейтуея пак може да се филтрира, вместо да е недостижим.
  const statusOptions = useMemo<ChecklistOption[]>(() => {
    const seen = new Set(rows.map((row) => row.status));
    return Array.from(seen)
      .map((status) => ({ value: statusLabel(status), label: statusLabel(status) }))
      .sort((a, b) => a.label.localeCompare(b.label));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, i18n.language]);

  const isMultiDay = useMemo(() => {
    if (!dateRange?.[0] || !dateRange?.[1]) return false;
    const { dateOf: dateOfMoment } = dayBounds(dayMode);
    return !dateOfMoment(dateRange[0]).isSame(dateOfMoment(dateRange[1]), "day");
  }, [dateRange, dayMode]);

  const columns = useCardPaymentsColumns({ statusOptions, isMultiDay });

  // Етикетите влизат в САМИТЕ редове: TanStack кешира стойността на клетката
  // за живота на `data`, така че смяна на езика иначе не стига до вече
  // изчислените клетки.
  const namedRows = useMemo<NamedCardPaymentRow[]>(
    () =>
      rows.map((row) => ({
        ...row,
        statusLabel: statusLabel(row.status),
        mountLabel: mountLabel(row.mount),
        billLabel:
          row.bill_name || (row.bill_id !== null ? `#${row.bill_id}` : ""),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rows, i18n.language],
  );

  const handleExport = () =>
    exportToExcel<NamedCardPaymentRow>({
      // БЕЗ собствен времеви печат — `exportToExcel` вече лепи `_${stamp}`.
      filename: t("reportsPage.cardPaymentsTitle"),
      sheetName: t("reportsPage.cardPayments"),
      columns: [
        {
          header: t("reportsPage.cardPaymentsColTime"),
          value: (row) => dayjs(row.created_at).format("DD.MM.YYYY HH:mm"),
          width: 18,
        },
        {
          header: t("reportsPage.cardPaymentsColBill"),
          value: (row) =>
            row.bill_name || (row.bill_id !== null ? `#${row.bill_id}` : ""),
          width: 16,
        },
        {
          header: t("reportsPage.cardPaymentsColStatus"),
          value: (row) => statusLabel(row.status),
          width: 16,
        },
        { header: t("reportsPage.cardPaymentsColPan"), value: (row) => row.masked_pan ?? "", width: 20 },
        { header: "RRN", value: (row) => row.rrn ?? "", width: 18 },
        { header: t("reportsPage.cardPaymentsColAuth"), value: (row) => row.auth_id ?? "", width: 14 },
        {
          header: t("reportsPage.cardPaymentsColReceipt"),
          value: (row) => row.receipt_number ?? "",
          width: 14,
        },
        {
          header: t("reportsPage.cardPaymentsColMount"),
          value: (row) => mountLabel(row.mount),
          width: 12,
        },
        {
          // Ръчно върнатото няма RRN — носи бележка. Без `refund_note` тези
          // редове излизаха празни точно в колоната, заради която се отваря.
          header: t("reportsPage.cardPaymentsColRefund"),
          value: (row) => row.refund_rrn || row.refund_note || "",
          width: 18,
        },
        {
          header: t("reportsPage.cardPaymentsColAmount"),
          value: (row) => parseDecimal(row.amount),
          width: 12,
        },
      ],
      rows: visibleRows,
    });

  return (
    <div className="flex h-full flex-col bg-canvas">
      <ReportTopBar
        title={t("reportsPage.cardPaymentsTitle")}
        isNarrow={narrow}
        onOpenMobileMenu={onOpenMobileMenu}
        onRefresh={() => refetch()}
        isFetching={isFetching}
      />

      <div
        className={`min-h-0 flex-1 overflow-y-auto overscroll-contain ${
          narrow ? "px-3.5 pb-6 pt-3.5" : "px-7 pb-8 pt-[22px]"
        }`}
      >
        <div className="flex flex-col gap-3.5">
          <div className="flex justify-center overflow-x-auto pb-0.5">
            <ReportDatePicker
              value={dateRange}
              onChange={setDateRange}
              defaultMode="day"
              dayMode={dayMode}
              withTime
            />
          </div>

          {isLoading ? (
            <div className="grid place-items-center py-20">
              <Spin size="large" />
            </div>
          ) : (
            <>
              <div
                className={`grid gap-2.5 ${
                  narrow ? "grid-cols-1" : "grid-cols-1 md:grid-cols-3"
                }`}
              >
                <ReportKpiCard
                  kicker={t("reportsPage.cardPaymentsConsumedKicker")}
                  value={fmtEuro(totals.consumedTotal)}
                  sub={pluralLabel(
                    t,
                    totals.consumedCount,
                    "reportsPage.cardPaymentsRowOne",
                    "reportsPage.cardPaymentsRowMany",
                  )}
                  hero
                />
                <ReportKpiCard
                  kicker={t("reportsPage.cardPaymentsUnresolvedKicker")}
                  value={fmtEuro(totals.unresolvedTotal)}
                  sub={pluralLabel(
                    t,
                    totals.unresolvedCount,
                    "reportsPage.cardPaymentsRowOne",
                    "reportsPage.cardPaymentsRowMany",
                  )}
                  hot={totals.unresolvedCount > 0}
                  hero
                />
                <ReportKpiCard
                  kicker={t("reportsPage.cardPaymentsVoidedKicker")}
                  value={fmtEuro(totals.voidedTotal)}
                  sub={pluralLabel(
                    t,
                    totals.voidedCount,
                    "reportsPage.cardPaymentsRowOne",
                    "reportsPage.cardPaymentsRowMany",
                  )}
                />
              </div>

              {/* Обяснителният абзац за сверката е свален нарочно: трите
                  кутийки „С БОН / ВИСЯЩИ / ВЪРНАТИ" вече я казват, а текстът
                  стоеше на всяко отваряне и се четеше веднъж. Правилото за
                  сверката остава в коментара най-горе на файла. */}
              <TerminalsCard
                terminals={terminals}
                mountLabel={mountLabel}
                isNarrow={narrow}
              />

              <DataTable<NamedCardPaymentRow>
                tableId="card-payments"
                data={namedRows}
                columns={columns}
                getRowId={(row) => String(row.id)}
                refreshing={isFetching}
                onFilteredRowsChange={setVisibleRows}
                ariaLabel={t("reportsPage.cardPaymentsTitle")}
                title={
                  <div className="flex min-w-0 flex-col gap-1">
                    <span className="font-pos-mono text-[10.5px] font-bold uppercase leading-none tracking-[0.1em] text-muted">
                      {t("reportsPage.cardPaymentsListKicker")}
                    </span>
                    <span className="text-[16px] font-extrabold leading-none text-ink">
                      {pluralLabel(
                        t,
                        visibleRows.length,
                        "reportsPage.cardPaymentsRowOne",
                        "reportsPage.cardPaymentsRowMany",
                      )}
                    </span>
                  </div>
                }
                toolbarRight={
                  <ExportExcelButton
                    onExport={handleExport}
                    disabled={visibleRows.length === 0}
                    bare
                  />
                }
                onRowClick={(row) =>
                  row.bill_id !== null && setSelectedBillId(row.bill_id)
                }
                rowTone={(row) =>
                  row.status === "unknown" || row.status === "void_failed"
                    ? "danger"
                    : null
                }
                emptyTitle={t("reportsPage.cardPaymentsNoData")}
                // БЕЗ собствен `emptyHint`: досегашният повтаряше заглавието
                // със същите думи. Подразбирането на таблицата казва какво да
                // НАПРАВИ човекът (филтри / период), вместо да се повтори.
                initialSorting={[{ id: "created_at", desc: true }]}
              />
            </>
          )}
        </div>
      </div>

      {isModalOpen && selectedBill ? (
        <BillOperationsModal
          bill={selectedBill}
          open
          onClose={() => setSelectedBillId(null)}
          onBillUpdated={() => refetch()}
          // БЕЗ `showCancelButton`: тази справка е за четене. Футърът с
          // „Сторнирай всичко", „Възстанови" и „Фактуриране" се показва само в
          // „История на маси", където правото е `view_tables_history`.
        />
      ) : null}
    </div>
  );
};
